import { z } from 'zod';
import { detectApp } from '../../../shared/appDetect';
import { accountEnvironmentMatches } from '../../../shared/appSession';
import type { Freigabe } from './appZugaenge';
const PARTNER = 'https://partner.werking.tools';
const LANDUNG = `${PARTNER}/?dgr=1`;

/** Only known local apps on the actual Partner port domain, never arbitrary redirects. */
export function appPortZiel(ziel: string): { url: string; appKey: string; origin: string } | null {
  try {
    const u = new URL(ziel), app = detectApp(ziel);
    if (u.protocol !== 'https:' || !/^\d{4,5}\.partner\.werking\.tools$/.test(u.hostname) || u.port || u.username || u.password || u.hash
      || !app || app.credentialSet !== 'dev' || app.isProduction) return null;
    return { url: u.href, appKey: app.appKey, origin: u.origin };
  } catch { return null; }
}
export function istAppPortGate(adresse: string, ziel: string): boolean {
  const app = appPortZiel(ziel);
  if (!app) return false;
  try {
    const u = new URL(adresse), next = new URL(u.searchParams.get('next') ?? '');
    return u.origin === PARTNER && !u.username && !u.password && !u.hash && u.pathname === '/'
      && u.searchParams.getAll('next').length === 1 && [...u.searchParams.keys()].every(k => k === 'next')
      && next.origin === app.origin && !next.username && !next.password && !next.hash;
  } catch { return false; }
}
export function pruefePortTicket(d: { url?: unknown; benutzer?: unknown }): string {
  const u = new URL(typeof d.url === 'string' ? d.url : 'about:blank');
  if (d.benutzer !== 'rafael' || u.origin !== PARTNER || u.username || u.password || u.hash || u.pathname !== '/api/fern-guide/anmelden'
    || u.searchParams.getAll('ticket').length !== 1 || !u.searchParams.get('ticket')
    || u.searchParams.getAll('ziel').length !== 1 || u.searchParams.get('ziel') !== '/?dgr=1'
    || [...u.searchParams.keys()].some(k => k !== 'ticket' && k !== 'ziel')) throw Error('Das Anmeldeticket gehört nicht zu diesem Partnerfenster.');
  return u.href;
}

type PortGateOptionen = {
  ziel: string; konto: string; freigabe: Freigabe; aufnahme: boolean; mensch: string | null;
  aktiv: () => boolean; aktuelleUrl: () => string;
  ticket: () => Promise<{ url?: unknown; benutzer?: unknown }>;
  navigate: (url: string) => Promise<unknown>;
  identity: () => Promise<{ user?: { id?: string }; impersonatedBy?: unknown }>;
};
/** One finite bootstrap, through the existing human-ticket endpoint. No machine impersonation or cookie copy. */
export async function meldeAppPortAn(o: PortGateOptionen): Promise<boolean> {
  const ziel = appPortZiel(o.ziel);
  if (!ziel || o.freigabe.kuerzel !== o.konto || o.freigabe.app !== ziel.appKey
    || !accountEnvironmentMatches(o.freigabe.umgebung, 'dev')) throw Error('App, Konto und Umgebung passen nicht zur Freigabe.');
  if (!o.aktiv() || !istAppPortGate(o.aktuelleUrl(), o.ziel)) return false;
  if (o.aufnahme) throw Error('Nur die Prüfaufnahme hat vorübergehenden Zugang. Die dauerhafte App-Anmeldung muss in Rafaels angemeldetem Fenster geprüft werden.');
  if (o.mensch !== 'rafael') throw Error('Die dauerhafte App-Anmeldung braucht Rafaels eigene angemeldete Sitzung. Der Berater muss diesen Nutzerweg prüfen.');
  const vorher = o.aktuelleUrl();
  const ticket = pruefePortTicket(await o.ticket());
  if (!o.aktiv() || o.aktuelleUrl() !== vorher) return false;
  await o.navigate(ticket);
  if (!o.aktiv() || o.aktuelleUrl() !== LANDUNG) return false;
  const me = await o.identity();
  if (!o.aktiv() || o.aktuelleUrl() !== LANDUNG) return false;
  if (me.user?.id !== 'rafael' || me.impersonatedBy) throw Error('Die eigene Partner-Anmeldung wurde in diesem Fenster nicht bestätigt.');
  await o.navigate(ziel.url);
  return o.aktiv();
}

/** Transient authentication addresses must never overwrite the stage's recipe/config. */
export function appPortZwischenseite(adresse: string, ziel: string): boolean {
  if (!appPortZiel(ziel)) return false;
  if (istAppPortGate(adresse, ziel)) return true;
  try { const u = new URL(adresse); return u.origin === PARTNER && !u.username && !u.password && !u.hash
    && (u.href === LANDUNG || u.pathname === '/api/fern-guide/anmelden'); } catch { return false; }
}

export const PortKontenAntwort = z.object({ konten: z.array(z.object({
  kuerzel: z.string().min(1), label: z.string(), email: z.string().min(1),
  app: z.string().optional(), umgebung: z.enum(['staging', 'produktion']),
})) });
export const PortTicketAntwort = z.object({ url: z.string().url(), benutzer: z.literal('rafael') });
